import { PrimitiveAtom, useAtom } from 'jotai'
import { useEffect, useState } from 'react'

import { store } from '@/store'
import {
    chatSettingsAtom,
    exampleTypeAtom,
    filteredSnippetsAtom,
    showSettingsSwitchMessage,
    snippetSourcesAtom,
    userMembershipAtom,
} from '@/store/gigiChat/ChatAtom'
import {
    IConversationSettings,
    ISnippetSourceBundle,
    ISnippetSourceDetails,
    TProcessingStrategy,
} from '@/types/gigiChat/ConversationSettings'
import { TChatGPTModel } from '@/types/gigiChat/Generic'
import { IFilteredSnippets } from '@/types/gigiChat/Snippets'
import { deepClone } from '@/utils'
import { whichBundleSelected } from '@/utils/snippets'
import { getFilterSnippetSources, getSnippetSourcesForBundle } from '@/utils/systemGroups'

import ChatSettings from './ChatSettings'

const Container = () => {
    const [snippetSources] = useAtom<ISnippetSourceDetails>(snippetSourcesAtom)
    const [filteredSnippets] = useAtom<IFilteredSnippets>(filteredSnippetsAtom)
    const [userGroups] = useAtom<Array<string>>(userMembershipAtom)
    const [options, setOptions] = useState<ISnippetSourceBundle[]>([])

    const [loading] = useState(false)
    const [chatSettings, setChatSettings] = useAtom<PrimitiveAtom<IConversationSettings | null>>(chatSettingsAtom)
    const [value, setValue] = useState('')

    useEffect(() => {
        if (chatSettings && chatSettings.processingStrategy === 'QA' && chatSettings.processingStrategyQa.snippetSources[0] !== value) {
            const snippet = chatSettings.processingStrategyQa.snippetSources[0]

            const selectedBundle = whichBundleSelected(
                [snippet],
                filteredSnippets.allowed_snippet_source_enums,
                filteredSnippets.allowed_bundle_configurations,
            )

            if (selectedBundle === 'DEFAULT') {
                const defaultBundle = snippetSources.defaultBundleName
                setValue(defaultBundle)
            } else {
                setValue(selectedBundle)
                store.set(exampleTypeAtom, selectedBundle)
            }
        }
    }, [chatSettings, filteredSnippets.allowed_bundle_configurations, filteredSnippets.allowed_snippet_source_enums, snippetSources, value])

    useEffect(() => {
        const filteredSnippetSources = getFilterSnippetSources(userGroups, snippetSources)
        if (filteredSnippetSources.length && userGroups) {
            setOptions(filteredSnippetSources)
        }
    }, [snippetSources, userGroups])

    useEffect(() => {
        if (options.length) {
            if (chatSettings?.processingStrategy === 'Writing') {
                setValue(chatSettings.processingStrategyWriting.model)
            }
        }
    }, [chatSettings, options, value])

    const handleSettingsChange = (processingStrategy: TProcessingStrategy, key: string, checkValue: string | number) => {
        if (loading) return

        const localSettings = deepClone<object>(chatSettings || {}) as IConversationSettings
        localSettings.processingStrategy = processingStrategy

        if (processingStrategy === 'Writing') {
            if (key === 'model') {
                localSettings.processingStrategyWriting.model = checkValue as TChatGPTModel
            }

            if (key === 'temperature') {
                localSettings.processingStrategyWriting.temperature = +checkValue
            }
        }

        if (processingStrategy === 'QA') {
            const snippets = getSnippetSourcesForBundle(key, userGroups, snippetSources)
            localSettings.processingStrategyQa.snippetSources = snippets

            if (key === snippetSources.defaultBundleName) {
                store.set(exampleTypeAtom, 'QA')
            } else {
                store.set(exampleTypeAtom, key)
            }
        }

        if (processingStrategy !== chatSettings?.processingStrategy) {
            showSettingsSwitchMessage(processingStrategy)
        }

        setChatSettings((prev) => ({
            ...prev,
            ...localSettings,
        }))
    }
    return <ChatSettings onSettingsChange={handleSettingsChange} chatSettings={chatSettings} options={options} defaultBundle={value} />
}

export default Container
